import React, { ForwardedRef, MouseEvent, ChangeEvent, useRef } from 'react';
import { Icon } from './Icon';
import { Body, Caption1, Caption2, Subhead } from './Text';
import { Loader } from './Loading'
import './Button.css';
import '../styles/styles.css';

interface ButtonProps {
  /** Title text to display on the button. */
  text1?: string;

  /** Optional leading icon. */
  icon1?: string;

  /** Optional trailing icon. */
  icon2?: string;

  /** Prominence of the button. */
  variant?: 'primary' | 'accent' | 'secondary' | 'tertiary' | 'tertiary-preferred' | 'quaternary' | 'destructive' | 'readOnly' | 'dynamic';

  /** Optional button becomes a wrapper for elements that need "button like" functionality */
  children?: React.ReactNode;

  /** Determines the button's behavior. */
  type?: 'button' | 'link' | 'submit' | 'fileUpload';

  /** If prop added, will render the small version of the button */
  small?: boolean;

  /** Click event handler (if type = button) */
  onClick?: (event: MouseEvent) => void;

  /** Only applicable when using a type = link. This is the src or anchor/navigation link */
  url?: string;

  /** If added, the button will fill the space of its parent */
  width?: 'hug' | 'fill';

  /** If true, will replace the content on the button with a loader */
  loading?: boolean;

  /** If prop added, the icon and text will be vertically stacked */
  stacked?: boolean;

  /** Aria label for accessibility */
  ariaLabel?: string;

  /** Aria label for when the button is in the loading state */
  ariaLoadingLabel?: string;

  //** Pass in a generic classname */
  className?: string;

  target?: '_self' | '_blank' | '_parent' | '_top';

  style?: React.CSSProperties;

  onFileUpload?: (file: File) => void; // Changed prop name to onChange

  icon1ColorCustom?: string;

}

export type { ButtonProps }

const ButtonInner: React.ForwardRefRenderFunction<HTMLButtonElement | HTMLAnchorElement, ButtonProps> =
  (props, ref) => {
    const {
      icon1,
      icon2,
      variant = 'secondary',
      type = 'button',
      text1,
      small,
      stacked,
      onClick,
      url,
      width = 'fill',
      loading = false,
      style = {},
      children,
      className,
      target = '_self',
      onFileUpload,
      icon1ColorCustom
    } = props;

    const fileInputRef = useRef<HTMLInputElement>(null);

    const generateDefaultAriaLabel = () => {
      let baseLabel = text1 || `${icon1 || ""} ${icon2 || ""}`.trim();
      return loading ? `${baseLabel}, is loading` : baseLabel;
    }

    const ariaLabel = loading && props.ariaLoadingLabel
      ? props.ariaLoadingLabel
      : (props.ariaLabel || generateDefaultAriaLabel());

    const ariaProps = {
      'aria-label': ariaLabel,
      'aria-busy': loading
    };

    const handleClick = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (type === 'submit') {
        event.preventDefault();
      }

      if (onClick) {
        onClick(event);
      }

      // Trigger file input click when button is clicked
      if (onFileUpload && fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && onFileUpload) {
        onFileUpload(file);
      }
    };

    const iconColors: Record<string, string> = {
      'primary': 'on-primary',
      'accent': 'on-accent',
      'secondary': 'icon',
      'tertiary': 'primary',
      'tertiary-preferred': 'primary',
      'quaternary': 'icon',
      'destructive': 'negative',
      'readOnly': 'read-only'
    };


    const loadingColors: Record<string, string> = {
      'primary': '--ts-color-on-primary',
      'accent': '--ts-color-on-accent',
      'secondary': '--ts-color-icon',
      'tertiary': '--ts-color-icon',
      'tertiary-preferred': '--ts-color-icon',
      'quaternary': '--ts-color-icon',
      'destructive': '--ts-color-icon',
      'readOnly': '--ts-color-icon'
    };

    const iconColor = iconColors[variant || ''];
    const loadingColor = loadingColors[variant || ''];

    const TextComponent = stacked ? (small ? Caption2 : Caption1) : (small ? Subhead : Body);

    const buttonClasses = `button ${variant} ${small ? 'small' : ''} h-stack width-${width} ${className || ''}`;
    const stackDirection = `${stacked ? 'v-stack' : 'h-stack'}`;

    const renderContent = () => (
      loading ?
        <div className={`${stackDirection} background nested width-${width}`}>
          <Loader color={loadingColor} />
        </div>
        :
        <div className={`${stackDirection} background ungroup width-${width}`}>
          {icon1 && <Icon nested size={small ? '24px' : 'var(--ts-size-base)'} name={icon1} color={iconColor} colorCustom={icon1ColorCustom} />}
          {text1 && <TextComponent nested className='content-view' width='hug'>{text1}</TextComponent>}
          {icon2 && <Icon nested size={small ? '24px' : 'var(--ts-size-base)'} name={icon2} color={iconColor} />}
        </div>

    );



    if (children) {
      // Simple wrapper for children without button styling
      return url ? (
        <a {...ariaProps}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          className={`button-generic width-${width} ${className}`}
          href={url} onClick={handleClick}
          style={style}
          target={target}>
          <span style={{ textDecoration: 'none' }}>{children}</span>
        </a>
      ) : (
        // @ts-ignore
        <button
          {...ariaProps}
          ref={ref as ForwardedRef<HTMLButtonElement>}
          type={type}
          onClick={handleClick}
          className={`button-generic width-${width} ${className}`}
          style={style}
        >
          {children}
        </button>
      );
    }

    if (onFileUpload) {
      return (<>
        <label className={`v-stack width-${width} height-hug ${className}`}>
          <input ref={fileInputRef} type='file' style={{ display: 'none' }} onChange={handleFileChange} />
          <button style={style} {...ariaProps} ref={ref as ForwardedRef<HTMLButtonElement>} type='button' onClick={handleClick} className={buttonClasses}>
            {renderContent()}
          </button>
        </label>
      </>)
    }


    return url ? (
      // <div className='touch-region'>
      <a {...ariaProps} style={style} ref={ref as ForwardedRef<HTMLAnchorElement>} className={buttonClasses} href={url} onClick={handleClick} target={target}>
        {renderContent()}
      </a>
      // </div>
    ) : (
      // <div className='touch-region'>
      // @ts-ignore
      <button {...ariaProps} style={style} ref={ref as ForwardedRef<HTMLButtonElement>} type={type} onClick={handleClick} className={buttonClasses}>
        {renderContent()}
      </button>
      // </div>
    );
  };

/**
 * # Button
 * A component that allows for action and navigation.
 * 
 * [See Documentation](https://themeable.systems/documentation)
 * 
 * ---
 * 
 * ### Style props:
 * `text1`: Optional title.
 * 
 * `icon1`: Optional leading icon.
 * 
 * `icon2`: Optional trailing icon.
 * 
 * `variant`: Button style (`primary` | `accent` | `secondary` | `tertiary` | `quaternary` | `destructive`).
 * 
 * `grow`: If true, the button will grow to fit its container.
 * 
 * `small`: If true, the button will be rendered in its small size.
 * 
 * `color`: Color override for the button.
 *  
 * `stacked`: If true, the icon and text will be vertically stacked.
 * 
 * ---
 * 
 * 
 * ### Functional props:
 * 
 * `type`: Determines the button's behavior (`button` | `link` | `submit`).
 * 
 * `onClick`: Click event handler.
 * 
 * `url`: If type = link, the navigation link.
 * 
 * `loading`: If true, a loading spinner will be shown in place of the button's content.
 */

ButtonInner.displayName = "Button";
export const Button = React.forwardRef(ButtonInner);
